// =============================================================================
// io/parsers/selfCheck.ts — does the parser's OUTPUT reconcile with the FILE?
// -----------------------------------------------------------------------------
// M2. The third and last piece of the ingest review's LOW 8, after the
// `PositionQuantity` cross-check (D146) and the ATAS completeness gate (D149).
//
// ── 🚨 WHY AN INDEPENDENT RE-READ, AND NOT A REUSE OF THE WALKER'S ──
//
// This module reads the raw rows AGAIN, with its own column aliases, and never
// calls a walker. That looks like duplication and it is the entire point: a
// check that shares the walker's reading agrees with the walker's mistakes.
// **An oracle built from the thing it is checking is not an oracle.**
//
// The cost of independence is that a column this module resolves differently
// will make a healthy file look wrong. That is the safe failure — it is VISIBLE,
// where the failures these checks exist to catch are all silent. Every defect in
// the 2026-08-09 review produced *plausible* trades and 3,600 passing tests.
//
// ── THE THREE CHECKS, AND WHICH REAL DEFECT EACH WOULD HAVE CAUGHT ──
//
//   contracts   Every contract in the file is bought and sold, left open, or
//               carried in from before it starts. If those three do not add up,
//               the walker lost or invented some. → D151 (fills discarded as
//               duplicates: 18 bought closed as 10 with a phantom 8-lot short).
//
//   pnl         For an instrument that ends FLAT, total profit is
//               `sells − buys` and does not depend on how the fills were paired
//               at all. So it can be computed straight from the rows and
//               compared. → D151 again ($855 read as $500), and any future
//               mis-pricing.
//
// ⚠ AND ONE IT DOES **NOT** CATCH, said plainly because the opposite belief is
// the dangerous one: **D149 is invisible here.** Those rows carried a quantity
// and no price, so this module discards them exactly as the walker did, and the
// two then agree about a file they have both misread. A check cannot audit a row
// it also throws away. That case belongs to `atasFillsIntegrity`, which tests
// for the missing price directly — these three do not replace it.
//
//   direction   Buy fills that opened positions, and not one long trade in the
//               output, is impossible. → D145 on its own, where an overnight
//               hold left a day with zero longs on which the trader was long
//               twice, and +$1,459.50 in place of −$290.00.
//
// ⚠ ONLY FOR PER-FILL SOURCES. A paired export (Tradovate's Performance Report,
// TopstepX, Jigsaw) has no fills to reconcile against — its rows ARE the round
// trips, so all three checks would compare a number with itself.
// =============================================================================

import { getCol, numCol, type Row } from './columns';
import type { ImportedTrade, OpenPosition } from './sierra';

export type SelfCheckKind = 'contracts' | 'pnl' | 'direction';

export interface SelfCheckFinding {
  kind: SelfCheckKind;
  /** Root symbol, as the trades carry it. */
  contract: string;
  /** Empty string where the source has no account column. */
  account: string;
  /** One plain sentence. Rendered as-is; no caller re-words it. */
  detail: string;
}

export interface SelfCheckResult {
  findings: SelfCheckFinding[];
  /**
   * Instrument-and-account groups the checks could evaluate.
   *
   * 🚨 REPORTED BESIDE THE FINDINGS, and D146 is why: "no problems found" and
   * "no problems found across 0 groups we could read" look identical and mean
   * opposite things. A check that silently evaluated nothing is worse than no
   * check, because it buys confidence it has not earned.
   */
  groupsChecked: number;
  /** Groups skipped for want of a $/point — no dollar oracle is possible. */
  groupsUnpriced: number;
}

/** One row, read independently of any walker. */
interface RawFill {
  account: string;
  /** The symbol as written in the file, e.g. `MESU6`. */
  symbol: string;
  side: 'buy' | 'sell';
  qty: number;
  price: number;
  /**
   * A monotonic sort key, or `null` when the row carries no readable time.
   *
   * 🚨 REQUIRED BY THE DIRECTION CHECK, AND FILE ORDER IS NOT A SUBSTITUTE.
   * **Sierra's activity log is written NEWEST FIRST**, as are Rithmic's
   * completed orders — so the first ROW is the day's LAST fill. An earlier
   * version of this module took file order as chronological, and on a real
   * corpus of 48 of Josh's logs that produced exactly one finding: a day of
   * five long trades flagged as "the first fill is a sell", because a long day
   * ENDS with a sell. Entirely healthy import, entirely wrong warning.
   *
   * ⚠ The walkers all sort before pairing. A check that does not is reading a
   * different file from the one it is auditing.
   */
  ts: number | null;
}

/**
 * Column aliases, deliberately the UNION across every per-fill source.
 *
 * ⚠ Ordered so the FILL quantity beats the ORDER quantity. Sierra exports both
 * and they differ on a partial fill; reading the order quantity overstates
 * every partially-filled row.
 */
const QTY_COLS = [
  'FilledQuantity', 'Filled Quantity', 'FillQuantity', 'Fill Quantity',
  'Filled Qty', 'filledQty', 'fillQty', 'Volume', 'Quantity', 'qty', 'Qty',
] as const;

const PRICE_COLS = [
  'FillPrice', 'Fill Price', 'AverageFillPrice', 'Avg Fill Price', 'avgFillPrice',
  'fillPrice', 'Price', 'price', 'avgPrice', 'Avg Price',
] as const;

const SIDE_COLS = [
  'BuySell', 'Buy/Sell', 'B/S', 'buyOrSell', 'Direction', 'Side', 'side',
  'Action', 'Type',
] as const;

const SYM_COLS = [
  'Symbol', 'symbol', 'Instrument', 'instrument', 'Contract', 'contract',
  'Product', 'product',
] as const;

const ACCT_COLS = [
  'Account', 'TradeAccount', 'Account ID', 'accountSpec', 'accountId',
  'Account Id', 'AccountID',
] as const;

const TIME_COLS = [
  'DateTime', 'FillDateTime', 'FillTime', 'Fill Time', 'Date Time',
  'fillTime', 'Timestamp', 'timestamp', 'Time', 'Date',
  'Order Time', 'orderTime', 'Create Time (EDT)', 'Create Time',
] as const;

/**
 * A monotonic key for ordering fills, or `null` when there is no readable time.
 *
 * ⚠ Not an instant — only an ordering. Every row in one file shares a
 * convention, so a constant offset cannot change their relative order, and
 * treating the stamp as UTC keeps a clock change from reordering the day.
 */
function fillTs(r: Row): number | null {
  const raw = String(getCol(r, ...TIME_COLS) || '').trim();
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})[\sT]+(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/.exec(raw);
  if (m) {
    // Microseconds, like the Sierra walker: two fills in one millisecond at
    // different prices is a fast fill, not a rare one.
    const frac = Number(String(m[7] ?? '0').padEnd(6, '0').slice(0, 6)) || 0;
    return Date.UTC(+(m[1] as string), +(m[2] as string) - 1, +(m[3] as string),
      +(m[4] as string), +(m[5] as string), +(m[6] as string)) * 1000 + frac;
  }
  const t = new Date(raw.replace(/\s+/g, ' ')).getTime();
  return Number.isFinite(t) ? t * 1000 : null;
}

/** `MESU6` → `MES`; anything unexpected passes through upper-cased. */
function rootOf(sym: string): string {
  const s = sym.trim().toUpperCase().replace(/\s+/g, '').replace(/@.*$/, '');
  const m = /^([A-Z0-9]{1,4}?)[FGHJKMNQUVXZ]\d{1,2}$/.exec(s);
  return m ? (m[1] as string) : s;
}

/**
 * Read one row as a fill, or `null` when it is not one.
 *
 * ⚠ A row whose STATUS says it did not fill is not a fill, and neither is one
 * with a quantity and no price. Both are excluded here exactly as the walkers
 * exclude them — otherwise every cancelled order in a Rithmic export would look
 * like a contract the parser lost.
 */
function readFill(r: Row): RawFill | null {
  const status = String(getCol(r, 'Status', 'status', 'OrderStatus', 'Order Status') || '')
    .toLowerCase();
  if (status && !/fill|completed|done/.test(status)) return null;

  const symbol = String(getCol(r, ...SYM_COLS) || '').trim();
  if (!symbol) return null;

  const qty = Math.abs(parseFloat(String(getCol(r, ...QTY_COLS) || '0').replace(/,/g, ''))) || 0;
  const price = numCol(r, ...PRICE_COLS);
  if (!qty || !price) return null;

  const bs = String(getCol(r, ...SIDE_COLS) || '').toLowerCase().trim();
  if (!bs) return null;
  // `b`, `buy`, `bot`, `long`, `1` — the union of what the walkers accept.
  const isBuy = /^b/.test(bs) || bs === 'long' || bs === '1';
  // ⚠ A side we cannot read is NOT assumed to be a sell. Counting it as one
  // would manufacture a conservation failure out of a column we misread.
  if (!isBuy && !/^s/.test(bs) && bs !== 'short' && bs !== '-1' && bs !== '2') return null;

  return {
    account: String(getCol(r, ...ACCT_COLS) || '').trim(),
    symbol,
    side: isBuy ? 'buy' : 'sell',
    qty,
    price,
    ts: fillTs(r),
  };
}

interface Group {
  buyQty: number;
  sellQty: number;
  /** Signed cash: sells add, buys subtract. × $/point is the gross. */
  cash: number;
  trades: ImportedTrade[];
  openLong: number;
  openShort: number;
  /**
   * Which side the FIRST readable fill was on — and it is the whole direction
   * check.
   *
   * 🚨 "There are buy fills but no long trades" is NOT a defect on its own, and
   * believing it was is a mistake this file's own control caught. A genuine
   * short session is FULL of buys: it sells first and buys back. Firing on the
   * volume would have flagged every short day the trader has ever had.
   *
   * The first fill is different. It had nothing to close against, so it MUST
   * have opened a position — and the side it opened is not a matter of opinion.
   */
  firstSide: 'buy' | 'sell' | null;
  /** Sort key of the fill `firstSide` came from. */
  firstTs: number | null;
  /** Set when ANY fill in the group had no readable time — see `firstSide`. */
  timeUnreadable: boolean;
}

const keyOf = (account: string, root: string): string => `${account}|${root}`;

/**
 * Reconcile a per-fill parse against the file it came from.
 *
 * @param rows           the raw rows, BEFORE de-duplication and skipping — a
 *                       check run on the rows that survived cannot see the ones
 *                       that did not, which is the whole D151 failure.
 * @param carriedInQty   contracts the parser reported as closing a position
 *                       opened before the file starts. Zero for parsers that
 *                       cannot detect it (D149: only Sierra exports the column).
 * @param dppFor         $/point for a root symbol, or 0 when uncatalogued.
 */
export function selfCheckFills(
  rows: readonly Row[],
  trades: readonly ImportedTrade[],
  openPositions: readonly OpenPosition[],
  carriedInQty: number,
  dppFor: (root: string) => number,
): SelfCheckResult {
  const groups = new Map<string, Group>();
  const blank = (): Group => ({
    buyQty: 0, sellQty: 0, cash: 0, trades: [], openLong: 0, openShort: 0,
    firstSide: null, firstTs: null, timeUnreadable: false,
  });
  const at = (k: string): Group => {
    let g = groups.get(k);
    if (!g) { g = blank(); groups.set(k, g); }
    return g;
  };

  for (const r of rows) {
    const f = readFill(r);
    if (!f) continue;
    const g = at(keyOf(f.account, rootOf(f.symbol)));
    // 🚨 THE EARLIEST FILL BY TIME, NOT THE FIRST ROW. Sierra and Rithmic both
    // write newest-first, so the first row is the day's LAST fill — and on a
    // long-only day that is a sell. Reading file order as chronological is what
    // produced this module's one false positive against Josh's real corpus.
    if (f.ts == null) g.timeUnreadable = true;
    else if (g.firstTs == null || f.ts < g.firstTs) { g.firstTs = f.ts; g.firstSide = f.side; }
    if (f.side === 'buy') { g.buyQty += f.qty; g.cash -= f.price * f.qty; }
    else { g.sellQty += f.qty; g.cash += f.price * f.qty; }
  }

  for (const t of trades) {
    at(keyOf(t.sourceAccount ?? '', rootOf(t.contract))).trades.push(t);
  }
  for (const p of openPositions) {
    const g = at(keyOf(p.account ?? '', rootOf(p.contract)));
    if (p.side === 'long') g.openLong += p.openQty;
    else g.openShort += p.openQty;
  }

  const findings: SelfCheckFinding[] = [];
  let groupsChecked = 0;
  let groupsUnpriced = 0;

  for (const [k, g] of groups) {
    // A group with no fills at all is a paired source's trades landing here, or
    // a symbol this module could not read. Either way there is nothing to
    // reconcile, and inventing a finding from it would be noise.
    if (!g.buyQty && !g.sellQty) continue;
    groupsChecked++;

    const parts = k.split('|');
    const account = parts[0] as string;
    const contract = parts[1] as string;
    const dpp = dppFor(contract);

    // ── 1. CONTRACTS ───────────────────────────────────────────────────────
    //
    // `buys − sells` is what the file says the position moved by. The parser's
    // own answer is the open position it is holding. They must agree once the
    // carried-in contracts are allowed for — those closed something opened
    // before the file, so they are sells (or buys) with no matching opposite
    // inside it.
    //
    // ⚠ CARRIED-IN IS A WHOLE-FILE TOTAL, not per instrument, so it is only
    // subtracted when there is exactly one group with fills. On a multi-symbol
    // file with a carried-in position this check stands down rather than
    // guessing which instrument it belonged to — a wrong attribution here would
    // fire on a healthy file, and a check that cries wolf gets ignored.
    const fileNet = g.buyQty - g.sellQty;
    const parserNet = g.openLong - g.openShort;
    const soleGroup = groups.size === 1;
    if (carriedInQty === 0 || soleGroup) {
      // A carried-in close can be on either side; |carriedIn| is the tolerance.
      const slack = soleGroup ? carriedInQty : 0;
      const gap = fileNet - parserNet;
      if (Math.abs(gap) > slack + 1e-9) {
        const off = round(Math.abs(gap) - slack);
        // ⚠ It says the two numbers DISAGREE, not which one is wrong. Either a
        // fill was dropped or a position was invented, and from here the two are
        // indistinguishable — naming a cause would be a guess printed as a fact.
        findings.push({
          kind: 'contracts', contract, account,
          detail:
            `The file has ${round(g.buyQty)} bought and ${round(g.sellQty)} sold, so it should ` +
            `end ${fmtSigned(fileNet)} — but the import ends ${fmtSigned(parserNet)}. ` +
            `That is ${off} contract${off === 1 ? '' : 's'} the import cannot account for.`,
        });
      }
    }

    // ── 2. P&L ─────────────────────────────────────────────────────────────
    //
    // 🚨 ONLY WHEN THE INSTRUMENT ENDS FLAT, and that is what makes it an
    // oracle rather than an approximation. With nothing open and nothing
    // carried in, every contract bought was sold, so the profit is just
    // `sells − buys` × $/point — **and it does not depend on how the fills
    // were paired.** FIFO, LIFO or at random all give the same total. So a
    // disagreement is never a pairing preference; it is a fill that was
    // dropped, duplicated or mis-priced.
    //
    // ⚠ ANY carried-in position stands the check down, whether or not it can be
    // attributed to this instrument. A carried-in close is a sell with no buy
    // inside the file, so `sells − buys` is no longer the profit — it is the
    // profit plus the value of a position we never saw opened. An earlier
    // version stood down only when the carried-in could be attributed, which
    // would have compared a whole-day total against a partial one.
    const flat = g.openLong === 0 && g.openShort === 0 && carriedInQty === 0;
    if (flat && g.buyQty === g.sellQty && g.trades.length) {
      if (dpp <= 0) {
        groupsUnpriced++;
      } else {
        const fileGross = g.cash * dpp;
        const parsedGross = g.trades.reduce((n, t) => n + (t.grossPnl ?? 0), 0);
        // A cent of tolerance: the walker accumulates leg by leg in floating
        // point and the file total is one subtraction.
        if (Math.abs(fileGross - parsedGross) > 0.01) {
          findings.push({
            kind: 'pnl', contract, account,
            detail:
              `The fills in the file come to ${money(fileGross)} before costs, but the ` +
              `${g.trades.length} imported trade${g.trades.length === 1 ? '' : 's'} come to ` +
              `${money(parsedGross)} — a gap of ${money(Math.abs(fileGross - parsedGross))}. ` +
              `Everything opened was closed, so these two cannot legitimately differ.`,
          });
        }
      }
    }

    // ── 3. DIRECTION ───────────────────────────────────────────────────────
    //
    // ⚠ THE CHEAPEST OF THE THREE AND IT CATCHES THE WORST BUG ON ITS OWN.
    // D145 turned a day the trader was long twice into a day with zero long
    // trades. Nothing else in the import noticed.
    //
    // ⚠ Guarded on the file having fills on BOTH sides, because a genuinely
    // one-directional file — an export that only ever sells into a carried-in
    // long — has no opening buys and no longs to show, correctly.
    //
    // 🚨 THE TEST IS THE FIRST FILL, NOT THE VOLUME — and the control in the
    // test file is what forced that. My first version fired when the file had
    // buy fills and the output had no long trades, which sounds right and would
    // have flagged **every genuine short session the trader has ever had**: a
    // short day sells first and buys back, so it is full of buys. The first
    // fill has nothing to close against, so the side it opened is a fact.
    //
    // ⚠ Stands down on any carried-in position, where the first fill may be
    // closing something opened before the file and opens nothing at all.
    // ⚠ And it stands down entirely if ANY fill in the group had no readable
    // time. With one unordered row the earliest is a guess, and a guess here
    // accuses a healthy import of pairing on the wrong side.
    if (carriedInQty === 0 && g.firstSide && !g.timeUnreadable && g.trades.length) {
      const longs = g.trades.filter((t) => t.side === 'long').length;
      const shorts = g.trades.length - longs;
      if (g.firstSide === 'buy' && longs === 0 && g.openLong === 0) {
        findings.push({
          kind: 'direction', contract, account,
          detail:
            `The first fill is a BUY, which can only open a long — but all ${shorts} imported ` +
            `trade${shorts === 1 ? ' is' : 's are'} short and nothing is left open. ` +
            `The fills have been paired on the wrong side.`,
        });
      } else if (g.firstSide === 'sell' && shorts === 0 && g.openShort === 0) {
        findings.push({
          kind: 'direction', contract, account,
          detail:
            `The first fill is a SELL, which can only open a short — but all ${longs} imported ` +
            `trade${longs === 1 ? ' is' : 's are'} long and nothing is left open. ` +
            `The fills have been paired on the wrong side.`,
        });
      }
    }
  }

  return { findings, groupsChecked, groupsUnpriced };
}

const round = (n: number): number => Math.round(n * 100) / 100;
const fmtSigned = (n: number): string =>
  n === 0 ? 'flat' : `${n > 0 ? 'long ' : 'short '}${round(Math.abs(n))}`;
const money = (n: number): string =>
  `${n < 0 ? '-' : ''}$${Math.abs(round(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
